// OpenResumes.jsx
import React, { useContext, useState } from 'react';
import { Modal } from 'flowbite-react';
import DataTableServerSide from '@/Components/DataTableServerSide.jsx';
import { DataContext } from '@/Components/DataContext.jsx';
import { Link, usePage } from '@inertiajs/react';
import { toast } from 'react-toastify';
import ResumeColumns from './ResumeColumns';
import ResumeRevisions from './ResumeRevisions';
import { AlertTriangle, FileText, History, X } from 'lucide-react';

const OpenResumesModal = ({ setModalOpen, modalOpen, filters = {} }) => {
  const { setResumeId, resumeId, saveResume, setSaveResume } = useContext(DataContext);
  const [deleted, setDeleted] = useState(null);
  const [showRevisions, setShowRevisions] = useState(false);
  const [currentResumeId, setCurrentResumeId] = useState(null);

  const {
    props: { auth },
  } = usePage();

  const handleDeleteResume = (resumeId) => {
    if (confirm('Are you sure you want to delete this resume?')) {
      fetch(`/resumes/delete/${resumeId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Failed to delete resume');
          }
          return response.json();
        })
        .then((data) => {
          toast.success('Resume deleted successfully!');
          setDeleted(resumeId);
        })
        .catch((error) => {
          console.error(error);
          toast.error('Failed to delete resume!');
        });
    }
  };

  const openRevisionHistory = (id) => {
    setCurrentResumeId(id);
    setShowRevisions(true);
  };

  // Get columns from separate component
  const columns = ResumeColumns({
    resumeId,
    setResumeId,
    setModalOpen,
    handleDeleteResume,
    openRevisionHistory,
  });

  return (
    <Modal
      show={modalOpen}
      onClose={() => {
        setShowRevisions(false);
        setModalOpen(false);
      }}
    >
      <Modal.Header className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 dark:bg-gray-800">
        <div className="text-xl font-semibold text-gray-800 dark:text-white">
          <span className="flex items-center">
            {!showRevisions ? (
              <>
                <FileText className="w-5 h-5 mr-2 text-indigo-600 dark:text-indigo-400" />
                Your Saved Resumes
              </>
            ) : (
              <>
                <History className="w-5 h-5 mr-2 text-amber-500 dark:text-amber-400" />
                Saved Revisions
              </>
            )}
          </span>
        </div>
      </Modal.Header>
      <Modal.Body className="px-6 py-4 dark:bg-gray-800">
        {!auth.user && (
          <div
            className="p-4 mb-6 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-900 dark:text-red-400 border-l-4 border-red-500 shadow-sm"
            role="alert"
          >
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              <span className="font-semibold">You are not logged in!</span>
            </div>
            <div className="mt-2 ml-7">
              You need to{' '}
              <Link
                href={route('login')}
                className="font-medium text-indigo-600 hover:text-indigo-800 underline transition-colors"
              >
                sign in
              </Link>{' '}
              to access your saved resumes.
            </div>
          </div>
        )}

        {showRevisions ? (
          <ResumeRevisions resumeId={currentResumeId} handleBack={() => setShowRevisions(false)} />
        ) : (
          <div className="mb-4">
            <DataTableServerSide
              columns={columns}
              routeName="resumes.all"
              recompute={deleted}
              filters={filters}
            />
          </div>
        )}
      </Modal.Body>
      <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex justify-end rounded-b-lg">
        <button
          type="button"
          onClick={() => {
            setShowRevisions(false);
            setModalOpen(false);
          }}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-indigo-600 transition-colors duration-200"
        >
          <X className="w-4 h-4" />
          Close
        </button>
      </div>
    </Modal>
  );
};

export default OpenResumesModal;
